import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { getMe, getAdminUsers, updateAdminUser, getAdminStats } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Shield, ShieldOff, UserCheck, UserX,
  BarChart3, BookOpen, Calendar, Clock, Search, RefreshCw
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [meRes, usersRes, statsRes] = await Promise.all([
        getMe(),
        getAdminUsers(),
        getAdminStats()
      ]);
      
      if (!meRes.data.is_admin) {
        toast.error('Admin access required');
        navigate('/');
        return;
      }
      
      setUser(meRes.data);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    setUpdating(prev => ({ ...prev, [userId]: true }));
    try {
      await updateAdminUser(userId, { is_active: !currentStatus });
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, is_active: !currentStatus } : u
      ));
      toast.success(currentStatus ? 'User suspended' : 'User activated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    setUpdating(prev => ({ ...prev, [`admin_${userId}`]: true }));
    try {
      await updateAdminUser(userId, { is_admin: !currentStatus });
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, is_admin: !currentStatus } : u
      ));
      toast.success(currentStatus ? 'Admin access removed' : 'Admin access granted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setUpdating(prev => ({ ...prev, [`admin_${userId}`]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(user?.role === 'teacher' ? '/teacher' : '/student')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Admin Dashboard
              </h1>
              <p className="text-text-secondary text-sm">Manage users and track platform usage</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">Total Users</p>
                  <p className="font-heading text-xl font-bold">{stats.users.total}</p>
                </div>
              </div>
            </Card>
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">Teachers</p>
                  <p className="font-heading text-xl font-bold">{stats.users.teachers}</p>
                </div>
              </div>
            </Card>
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neon-green/20 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-neon-green" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">Students</p>
                  <p className="font-heading text-xl font-bold">{stats.users.students}</p>
                </div>
              </div>
            </Card>
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neon-yellow/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-neon-yellow" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">Active (7d)</p>
                  <p className="font-heading text-xl font-bold">{stats.users.active_this_week}</p>
                </div>
              </div>
            </Card>
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neon-lime/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-neon-lime" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">New (7d)</p>
                  <p className="font-heading text-xl font-bold">{stats.users.new_this_week}</p>
                </div>
              </div>
            </Card>
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neon-orange/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-neon-orange" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">Exercises</p>
                  <p className="font-heading text-xl font-bold">{stats.content.exercises}</p>
                </div>
              </div>
            </Card>
            <Card className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-neon-red/20 flex items-center justify-center">
                  <UserX className="w-5 h-5 text-neon-red" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase">Suspended</p>
                  <p className="font-heading text-xl font-bold">{stats.users.suspended}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* User Management */}
        <Card className="card-glass overflow-hidden">
          <div className="p-4 border-b border-white/10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <h3 className="font-heading font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Teacher Management
              </h3>
              <p className="text-text-muted text-sm">{filteredUsers.length} teachers</p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input 
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-text-secondary">Teacher</TableHead>
                  <TableHead className="text-text-secondary text-center">Classrooms</TableHead>
                  <TableHead className="text-text-secondary text-center">Students</TableHead>
                  <TableHead className="text-text-secondary text-center">Exercises</TableHead>
                  <TableHead className="text-text-secondary text-center">Logins</TableHead>
                  <TableHead className="text-text-secondary">Registered</TableHead>
                  <TableHead className="text-text-secondary">Last Login</TableHead>
                  <TableHead className="text-text-secondary text-center">Active</TableHead>
                  <TableHead className="text-text-secondary text-center">Admin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => (
                  <TableRow key={u.user_id} className="border-white/5 hover:bg-white/5">
                    <TableCell>
                      <div>
                        <p className="font-medium">{u.name || 'Unknown'}</p>
                        <p className="text-text-muted text-xs">{u.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {u.classrooms_count}
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {u.students_count}
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {u.exercises_count}
                    </TableCell>
                    <TableCell className="text-center font-mono">
                      {u.login_count}
                    </TableCell>
                    <TableCell className="text-text-secondary text-sm">
                      {formatDate(u.created_at)}
                    </TableCell>
                    <TableCell className="text-text-secondary text-sm">
                      {formatDateTime(u.last_login)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={u.is_active}
                        onCheckedChange={() => handleToggleActive(u.user_id, u.is_active)}
                        disabled={updating[u.user_id] || u.user_id === user?.user_id}
                        data-testid={`active-toggle-${u.user_id}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={u.is_admin}
                        onCheckedChange={() => handleToggleAdmin(u.user_id, u.is_admin)}
                        disabled={updating[`admin_${u.user_id}`] || u.user_id === user?.user_id}
                        data-testid={`admin-toggle-${u.user_id}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary">No teachers found</p>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
